import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  Calendar as CalendarIcon, 
  Target,
  CheckCircle2,
  Clock,
  BookOpen,
  Zap,
  ChevronRight
} from "lucide-react";
import { format, addDays, parseISO } from "date-fns";

const StudyPlanPage = () => {
  const navigate = useNavigate();
  const [studyPlan, setStudyPlan] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [examDate, setExamDate] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [planData, subjectsData] = await Promise.all([
          api.get("/study-plan"),
          api.get("/subjects")
        ]);
        setStudyPlan(planData.study_plan);
        setSubjects(subjectsData.subjects);
        if (planData.study_plan?.exam_date) {
          setExamDate(parseISO(planData.study_plan.exam_date));
        }
      } catch (error) {
        console.error("Failed to fetch study plan:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleGeneratePlan = async () => {
    if (!examDate) {
      toast.error("Please select your exam date first");
      return;
    }

    setGenerating(true);
    try {
      const data = await api.post("/study-plan/generate", {
        exam_date: examDate.toISOString()
      });
      setStudyPlan(data.study_plan);
      toast.success("Study Plan Generated!", {
        description: "Your personalized plan is ready"
      });
    } catch (error) {
      console.error("Failed to generate plan:", error);
      toast.error("Failed to generate study plan");
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleTaskComplete = async (day) => {
    try {
      const task = studyPlan.daily_tasks.find(t => t.day === day);
      await api.put(`/study-plan/task/${day}`, {
        completed: !task.completed
      });
      
      // Update local state
      setStudyPlan({
        ...studyPlan,
        daily_tasks: studyPlan.daily_tasks.map(t => 
          t.day === day ? { ...t, completed: !t.completed } : t
        )
      });
      
      toast.success(task.completed ? "Task unmarked" : "Task completed!", {
        description: task.completed ? "" : "+50 XP earned!"
      });
    } catch (error) {
      console.error("Failed to update task:", error);
    }
  };

  const getTasksForDate = (date) => {
    if (!studyPlan?.daily_tasks) return null;
    const dateStr = format(date, "yyyy-MM-dd");
    return studyPlan.daily_tasks.find(t => t.date === dateStr);
  };

  const selectedDateTask = getTasksForDate(selectedDate);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading study plan...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="study-plan-page" className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Study Plan</h1>
          <p className="text-muted-foreground">Your personalized preparation schedule</p>
        </div>
        {examDate && (
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary">
            <CalendarIcon className="w-4 h-4" />
            <span className="text-sm font-medium">
              Exam: {format(examDate, "MMM d, yyyy")}
            </span>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="md:col-span-1">
          <div className="glass rounded-2xl p-4 border border-border">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-md"
              modifiers={{
                hasTasks: studyPlan?.daily_tasks?.map(t => parseISO(t.date)) || [],
                completed: studyPlan?.daily_tasks?.filter(t => t.completed).map(t => parseISO(t.date)) || []
              }}
              modifiersStyles={{
                hasTasks: { fontWeight: 'bold' },
                completed: { backgroundColor: 'hsl(var(--chart-3) / 0.2)' }
              }}
            />

            {/* Exam Date Picker */}
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground mb-2">Set Exam Date</p>
              <Calendar
                mode="single"
                selected={examDate}
                onSelect={setExamDate}
                disabled={(date) => date < new Date()}
                className="rounded-md"
              />
              <Button
                data-testid="generate-plan-btn"
                onClick={handleGeneratePlan}
                disabled={!examDate || generating}
                className="w-full mt-4 rounded-full"
              >
                {generating ? "Generating..." : "Generate Plan"}
              </Button>
            </div>
          </div>
        </div>

        {/* Daily Task View */}
        <div className="md:col-span-2 space-y-6">
          {/* Selected Day Tasks */}
          {selectedDateTask ? (
            <div className="glass rounded-2xl p-6 border border-border">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-muted-foreground">Day {selectedDateTask.day}</p>
                  <h2 className="text-xl font-semibold">{format(selectedDate, "EEEE, MMMM d")}</h2>
                </div>
                <Button
                  data-testid="toggle-complete-btn"
                  variant={selectedDateTask.completed ? "outline" : "default"}
                  size="sm"
                  onClick={() => handleToggleTaskComplete(selectedDateTask.day)}
                  className="rounded-full"
                >
                  {selectedDateTask.completed ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2 text-success" />
                      Completed
                    </>
                  ) : (
                    "Mark Complete"
                  )}
                </Button>
              </div>

              {/* Focus Subject */}
              <div className="mb-6 p-4 rounded-xl bg-primary/10 border border-primary/20">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-5 h-5 text-primary" />
                  <span className="font-medium">Focus Subject</span>
                </div>
                <p className="text-lg font-semibold">
                  {subjects.find(s => s.id === selectedDateTask.primary_subject)?.name || selectedDateTask.primary_subject}
                </p>
              </div>

              {/* Tasks */}
              <div className="space-y-3">
                {selectedDateTask.tasks?.map((task, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 rounded-xl bg-secondary border border-border"
                  >
                    <div className="flex items-center gap-3">
                      {task.type === "mcq" && <BookOpen className="w-5 h-5 text-primary" />}
                      {task.type === "flashcard" && <Zap className="w-5 h-5 text-accent" />}
                      {task.type === "review" && <Target className="w-5 h-5 text-success" />}
                      <div>
                        <p className="font-medium capitalize">{task.type} Practice</p>
                        <p className="text-sm text-muted-foreground">
                          {task.count ? `${task.count} items` : task.subject}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      {task.estimated_time} min
                    </div>
                  </div>
                ))}
              </div>

              {/* Total Time */}
              <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total estimated time</span>
                <span className="font-semibold">{selectedDateTask.total_time} minutes</span>
              </div>

              {/* Quick Actions */}
              <div className="mt-4 flex gap-3">
                <Button
                  onClick={() => navigate(`/practice/${selectedDateTask.primary_subject}`)}
                  className="flex-1 rounded-full"
                >
                  Start Practice
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/flashcards")}
                  className="rounded-full border-border"
                >
                  Flashcards
                </Button>
              </div>
            </div>
          ) : (
            <div className="glass rounded-2xl p-8 border border-border text-center">
              <CalendarIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                {studyPlan ? "No tasks scheduled for this day" : "Generate a study plan to get started"}
              </p>
              {!studyPlan && (
                <Button onClick={handleGeneratePlan} disabled={!examDate} className="rounded-full">
                  Generate Plan
                </Button>
              )}
            </div>
          )}

          {/* Weekly Goals */}
          {studyPlan?.weekly_goals && (
            <div className="glass rounded-2xl p-6 border border-border">
              <h2 className="text-lg font-semibold mb-4">Weekly Goals</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {studyPlan.weekly_goals[0]?.targets && Object.entries(studyPlan.weekly_goals[0].targets).map(([key, value]) => (
                  <div key={key} className="p-4 rounded-xl bg-secondary">
                    <p className="text-sm text-muted-foreground capitalize mb-1">
                      {key.replace(/_/g, " ")}
                    </p>
                    <p className="text-2xl font-bold">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Focus Areas */}
          {studyPlan?.focus_subjects?.length > 0 && (
            <div className="glass rounded-2xl p-6 border border-destructive/20 bg-destructive/5">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-destructive" />
                Priority Focus Areas
              </h2>
              <div className="flex flex-wrap gap-2">
                {studyPlan.focus_subjects.map((subjectId) => {
                  const subject = subjects.find(s => s.id === subjectId);
                  return (
                    <Button
                      key={subjectId}
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/practice/${subjectId}`)}
                      className="rounded-full border-destructive/30 hover:bg-destructive/10"
                    >
                      {subject?.name || subjectId}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudyPlanPage;
